export const timelineEn = {
  title: "Timeline",
  compare: "Compare",
  viewMode: {
    days: "Days",
    week: "Week",
    month: "Month",
    ariaLabel: "Timeline view mode",
  },
  error: {
    title: "Unable to load timeline",
    description: "Something went wrong while loading your pain history.",
    retry: "Try Again",
  },
  empty: {
    title: "Your pain history will appear here",
    description: "Start by logging your first check-in.",
    addCheckin: "Add Check-in",
  },
  filters: {
    title: "Filters",
    ariaLabel: "Timeline filters",
    applied_one: "({{count}} applied)",
    applied_other: "({{count}} applied)",
    activeFilters: "Active Filters",
    clearAll: "Clear all",
    bodyRegions: "Body Regions",
    bodyRegionsCount: "Body Regions ({{count}})",
    medications: "Medications",
    medicationsCount: "Medications ({{count}})",
    noOptions: "No filter options available",
    removeFilter: "Remove {{label}} filter",
    noMatch: "No records match the current filters.",
    clearFilters: "Clear filters",
  },
  days: {
    loadMore: "Load more",
    loading: "Loading...",
    tookPrefix: "Took",
    location_one: "{{count}} location",
    location_other: "{{count}} locations",
    avg: "avg {{value}}",
  },
  calendarDay: {
    noData: "no data recorded",
    painFree: "pain-free day",
    avgPain_one: "average pain {{level}} out of 10, {{count}} location",
    avgPain_other: "average pain {{level}} out of 10, {{count}} locations",
  },
  week: {
    previousWeek: "Previous week",
    nextWeek: "Next week",
    noEntries: "No entries for this week",
    addEntry: "Add Entry for This Week",
    dailyBreakdown: "Daily Breakdown",
    tapForDetails: "Tap any day for details",
    tapEmptyToAdd: "Tap + to log a missed day",
    noData: "No data for this week",
    addCheckins: "Add check-ins to see weekly patterns",
    quickAddAria: "Log check-in for {{date}}",
    swipeHint: "Swipe to navigate between weeks",
  },
  consistency: {
    title: "Logging Consistency",
    fillRate: "{{logged}}/{{eligible}} days",
    fillRateFull: "{{logged}}/{{total}} days",
    streak: "{{count}}-day streak",
    streakNone: "No streak",
    perfect: "Perfect week!",
    missingDays: "Missing:",
    missingDaysAria: "Missing log entries for: {{days}}",
    streakAria: "Current logging streak: {{count}} consecutive days",
    fillRateAria: "Logged {{logged}} of {{eligible}} eligible days this week",
  },
  month: {
    previousMonth: "Previous month",
    nextMonth: "Next month",
    today: "Today",
    goToCurrentMonth: "Go to current month",
    noEntries: "No entries for {{month}}",
    addEntry: "Add Entry",
    tapForDetails: "Tap any day for details",
    tapEmptyToAdd: "Tap + to log a missed day",
    noData: "No data for this month",
    addCheckins: "Add check-ins to see monthly patterns",
    quickAddAria: "Log check-in for {{date}}",
    swipeHint: "Swipe to navigate between months",
    legend: {
      low: "Low (1-3)",
      moderate: "Moderate (4-6)",
      high: "High (7-10)",
    },
  },
  summary: {
    monthSummary: "Month Summary",
    frequency: "Frequency",
    intensity: "Intensity",
    frequencyAriaLabel: "Show frequency heatmap",
    intensityAriaLabel: "Show intensity heatmap",
    mostAffected: "Most affected:",
    averagePain: "Average pain:",
    daysLogged: "Days logged:",
    daysLoggedOf: "{{logged}} of {{total}}",
    painFreeDays: "Pain-free days:",
    frequencyHelp: "Showing how many days each location was affected",
    intensityHelp: "Showing average pain intensity for each location",
    day_one: "day",
    day_other: "days",
  },
  weekComparison: {
    title: "vs. Previous Week",
    avgPain: "Avg Pain",
    daysLogged: "Days Logged",
    locations: "Locations",
    noPreviousData: "No previous week data for comparison",
    avgPainAria: "Average pain changed by {{delta}} compared to last week",
    daysLoggedAria: "Days logged changed by {{delta}} compared to last week",
    locationCountAria: "Pain locations changed by {{delta}} compared to last week",
  },
  narrative: {
    title: "Month at a Glance",
    logged: "You logged {{logged}} of {{total}} days.",
    avgPain: "Average pain was {{value}}/10",
    avgPainDeltaDown: " ({{delta}} vs last month)",
    avgPainDeltaUp: " ({{delta}} vs last month)",
    avgPainDeltaSame: " (unchanged vs last month)",
    noPainData: "No pain was recorded this month.",
    worstRegion: "{{region}} was most affected ({{count}} days).",
    medicationDays: "Medication was taken on {{count}} days.",
    medicationDaysNone: "No medications were recorded.",
    painFreeDays: "{{count}} pain-free days logged",
    painFreeDaysDeltaUp: " ({{delta}} vs last month)",
    painFreeDaysDeltaDown: " ({{delta}} vs last month)",
    painFreeDaysDeltaSame: " (unchanged vs last month)",
    painFreeDaysNone: "No pain-free days were logged.",
    noPreviousMonth: "Previous month data not available for comparison.",
  },
  weekdayPattern: {
    title: "Day-of-Week Pattern",
    noData: "Not enough data to show weekday patterns",
    avgPainLabel: "avg {{value}}",
    worstDayAria: "{{day}} has the highest average pain at {{value}}",
    dayAria: "{{day}}: average pain {{value}} from {{count}} records",
    dayNoDataAria: "{{day}}: no data",
    mon: "Mon",
    tue: "Tue",
    wed: "Wed",
    thu: "Thu",
    fri: "Fri",
    sat: "Sat",
    sun: "Sun",
  },
  events: {
    event_one: "{{count}} Event",
    event_other: "{{count}} Events",
    viewAll: "View all on Events page",
    viewDetailsFor: "View details for {{title}}",
    // Short category labels for popover
    categoryMedication: "Medication",
    categoryMedical: "Medical",
    categoryLifestyle: "Lifestyle",
    categoryHealth: "Health",
    categoryMilestone: "Milestone",
    categoryOther: "Other",
  },
  sparkline: {
    /** Accessible text alternative for the mini sparkline chart */
    summary: "{{window}}-day trend: range {{min}} to {{max}}, {{direction}}.",
    trendUp: "trending up",
    trendDown: "trending down",
    trendFlat: "stable",
  },
} as const;

export const timelineFi = {
  title: "Aikajana",
  compare: "Vertaa",
  viewMode: {
    days: "Päivät",
    week: "Viikko",
    month: "Kuukausi",
    ariaLabel: "Aikajanan näkymätila",
  },
  error: {
    title: "Aikajanan lataus epäonnistui",
    description: "Jokin meni pieleen kipuhistorian latauksen aikana.",
    retry: "Yritä uudelleen",
  },
  empty: {
    title: "Kipuhistoriasi näkyy täällä",
    description: "Aloita kirjaamalla ensimmäinen kirjaus.",
    addCheckin: "Lisää kirjaus",
  },
  filters: {
    title: "Suodattimet",
    ariaLabel: "Aikajanan suodattimet",
    applied_one: "({{count}} käytössä)",
    applied_other: "({{count}} käytössä)",
    activeFilters: "Aktiiviset suodattimet",
    clearAll: "Tyhjennä kaikki",
    bodyRegions: "Kehoalueet",
    bodyRegionsCount: "Kehoalueet ({{count}})",
    medications: "Lääkkeet",
    medicationsCount: "Lääkkeet ({{count}})",
    noOptions: "Ei suodatusvaihtoehtoja",
    removeFilter: "Poista {{label}} suodatin",
    noMatch: "Yhtään merkintää ei vastaa suodattimia.",
    clearFilters: "Tyhjennä suodattimet",
  },
  days: {
    loadMore: "Lataa lisää",
    loading: "Ladataan...",
    tookPrefix: "Otti",
    location_one: "{{count}} sijainti",
    location_other: "{{count}} sijaintia",
    avg: "ka. {{value}}",
  },
  calendarDay: {
    noData: "ei kirjattua tietoa",
    painFree: "kivuton päivä",
    avgPain_one: "keskimääräinen kipu {{level}} / 10, {{count}} sijainti",
    avgPain_other: "keskimääräinen kipu {{level}} / 10, {{count}} sijaintia",
  },
  week: {
    previousWeek: "Edellinen viikko",
    nextWeek: "Seuraava viikko",
    noEntries: "Ei kirjauksia tälle viikolle",
    addEntry: "Lisää kirjaus tälle viikolle",
    dailyBreakdown: "Päivittäinen erittely",
    tapForDetails: "Napauta päivää saadaksesi lisätietoja",
    tapEmptyToAdd: "Napauta + kirjataksesi puuttuvan päivän",
    noData: "Ei tietoja tälle viikolle",
    addCheckins: "Lisää kirjauksia nähdäksesi viikkomallit",
    quickAddAria: "Kirjaa merkintä päivälle {{date}}",
    swipeHint: "Pyyhkäise vaihtaaksesi viikkoa",
  },
  consistency: {
    title: "Kirjausten johdonmukaisuus",
    fillRate: "{{logged}}/{{eligible}} päivää",
    fillRateFull: "{{logged}}/{{total}} päivää",
    streak: "{{count}} päivän putki",
    streakNone: "Ei putkea",
    perfect: "Täydellinen viikko!",
    missingDays: "Puuttuu:",
    missingDaysAria: "Puuttuvat kirjaukset: {{days}}",
    streakAria: "Nykyinen kirjausputki: {{count}} peräkkäistä päivää",
    fillRateAria: "Kirjattu {{logged}} / {{eligible}} mahdollisesta päivästä tällä viikolla",
  },
  month: {
    previousMonth: "Edellinen kuukausi",
    nextMonth: "Seuraava kuukausi",
    today: "Tänään",
    goToCurrentMonth: "Siirry nykyiseen kuukauteen",
    noEntries: "Ei kirjauksia: {{month}}",
    addEntry: "Lisää kirjaus",
    tapForDetails: "Napauta päivää saadaksesi lisätietoja",
    tapEmptyToAdd: "Napauta + kirjataksesi puuttuvan päivän",
    noData: "Ei tietoja tälle kuukaudelle",
    addCheckins: "Lisää kirjauksia nähdäksesi kuukausimallit",
    quickAddAria: "Kirjaa merkintä päivälle {{date}}",
    swipeHint: "Pyyhkäise vaihtaaksesi kuukautta",
    legend: {
      low: "Matala (1-3)",
      moderate: "Kohtalainen (4-6)",
      high: "Korkea (7-10)",
    },
  },
  summary: {
    monthSummary: "Kuukausiyhteenveto",
    frequency: "Tiheys",
    intensity: "Voimakkuus",
    frequencyAriaLabel: "Näytä tiheyslämpökartta",
    intensityAriaLabel: "Näytä voimakkuuslämpökartta",
    mostAffected: "Eniten vaikuttanut:",
    averagePain: "Keskimääräinen kipu:",
    daysLogged: "Kirjatut päivät:",
    daysLoggedOf: "{{logged}} / {{total}}",
    painFreeDays: "Kivuttomat päivät:",
    frequencyHelp: "Näyttää kuinka monena päivänä kukin sijainti oli vaikuttunut",
    intensityHelp: "Näyttää keskimääräisen kivun voimakkuuden kullekin sijainnille",
    day_one: "päivä",
    day_other: "päivää",
  },
  weekComparison: {
    title: "vs. Edellinen viikko",
    avgPain: "Ka. kipu",
    daysLogged: "Kirjatut pv",
    locations: "Sijainnit",
    noPreviousData: "Ei edellisen viikon tietoja vertailuun",
    avgPainAria: "Keskimääräinen kipu muuttui {{delta}} verrattuna edelliseen viikkoon",
    daysLoggedAria: "Kirjatut päivät muuttuivat {{delta}} verrattuna edelliseen viikkoon",
    locationCountAria: "Kipusijainnit muuttuivat {{delta}} verrattuna edelliseen viikkoon",
  },
  narrative: {
    title: "Kuukausi yhdellä silmäyksellä",
    logged: "Kirjasit {{logged}} / {{total}} päivää.",
    avgPain: "Keskimääräinen kipu oli {{value}}/10",
    avgPainDeltaDown: " ({{delta}} vs edellinen kk)",
    avgPainDeltaUp: " ({{delta}} vs edellinen kk)",
    avgPainDeltaSame: " (muuttumaton vs edellinen kk)",
    noPainData: "Kipua ei kirjattu tässä kuussa.",
    worstRegion: "{{region}} oli eniten vaikuttunut ({{count}} pv).",
    medicationDays: "Lääkitys kirjattiin {{count}} päivänä.",
    medicationDaysNone: "Lääkitystä ei kirjattu.",
    painFreeDays: "{{count}} kivutonta päivää kirjattu",
    painFreeDaysDeltaUp: " ({{delta}} vs edellinen kk)",
    painFreeDaysDeltaDown: " ({{delta}} vs edellinen kk)",
    painFreeDaysDeltaSame: " (muuttumaton vs edellinen kk)",
    painFreeDaysNone: "Kivuttomia päiviä ei kirjattu.",
    noPreviousMonth: "Edellisen kuukauden tietoja ei ole saatavilla vertailuun.",
  },
  weekdayPattern: {
    title: "Viikonpäivämalli",
    noData: "Ei tarpeeksi tietoja viikonpäivämallien näyttämiseen",
    avgPainLabel: "ka. {{value}}",
    worstDayAria: "{{day}} on korkein keskimääräinen kipu: {{value}}",
    dayAria: "{{day}}: keskimääräinen kipu {{value}}, {{count}} kirjausta",
    dayNoDataAria: "{{day}}: ei tietoja",
    mon: "Ma",
    tue: "Ti",
    wed: "Ke",
    thu: "To",
    fri: "Pe",
    sat: "La",
    sun: "Su",
  },
  events: {
    event_one: "{{count}} tapahtuma",
    event_other: "{{count}} tapahtumaa",
    viewAll: "Näytä kaikki Tapahtumat-sivulla",
    viewDetailsFor: "Näytä tiedot: {{title}}",
    // Short category labels for popover
    categoryMedication: "Lääkitys",
    categoryMedical: "Lääketiede",
    categoryLifestyle: "Elämäntapa",
    categoryHealth: "Terveys",
    categoryMilestone: "Virstanpylväs",
    categoryOther: "Muu",
  },
  sparkline: {
    /** Saavutettava tekstivaihtoehto mini-sparkline-kaaviolle */
    summary: "{{window}} päivän trendi: vaihteluväli {{min}}–{{max}}, {{direction}}.",
    trendUp: "nouseva",
    trendDown: "laskeva",
    trendFlat: "vakaa",
  },
} as const;
